import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Minimize2, Maximize2, Bot, User, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ChatWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const storedSessionId = localStorage.getItem('c3_chat_session');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadChatHistory(storedSessionId);
    }
  }, []);

  const loadChatHistory = async (sid) => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/history/${sid}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setMessages(response.data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          actions: msg.actions || []
        })));
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hey! 👋 I'm here to help answer any questions about C3 Contracting, our services, or your project ideas. What's on your mind?",
        actions: []
      }]);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleActionClick = (action) => {
    if (action.action === 'navigate') {
      setIsOpen(false);
      navigate(action.url);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage, actions: [] }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/chat/send`, {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        localStorage.setItem('c3_chat_session', response.data.session_id);
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        actions: response.data.actions || []
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I'm having a connection issue. Try again in a sec, or feel free to give us a call!",
        actions: [{ label: "Contact Us", action: "navigate", url: "/contact" }]
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickQuestion = (question) => {
    setInputValue(question);
    setTimeout(() => handleSend(), 100);
  };

  const handleNewChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API_URL}/api/chat/session/${sessionId}`);
      } catch (error) {
        console.error('Error clearing session:', error);
      }
    }
    localStorage.removeItem('c3_chat_session');
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: "Hey! 👋 I'm here to help answer any questions about C3 Contracting, our services, or your project ideas. What's on your mind?",
      actions: []
    }]);
  };

  // Quick question suggestions
  const quickQuestions = [
    "What services do you offer?",
    "How long does a project take?",
    "What areas do you serve?"
  ];

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-cyan-600 hover:bg-cyan-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 group"
        aria-label="Open chat"
        style={{ touchAction: 'manipulation' }}
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-12 right-0 bg-slate-800 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Questions? Chat with us!
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-[380px] h-[550px] sm:h-[600px]'
      }`}
      style={{ maxHeight: 'calc(100vh - 100px)', maxWidth: 'calc(100vw - 48px)' }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5 text-cyan-400" />
          </div>
          {!isMinimized && (
            <div>
              <h3 className="font-semibold text-sm">C3 Assistant</h3>
              <p className="text-xs text-slate-400">Ask me anything</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleMinimize}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            aria-label={isMinimized ? 'Maximize' : 'Minimize'}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[380px] sm:h-[430px] bg-slate-50">
            {messages.map((message, index) => (
              <div key={index}>
                <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex items-start gap-2 max-w-[90%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user' 
                        ? 'bg-cyan-600 text-white' 
                        : 'bg-slate-700 text-cyan-400'
                    }`}>
                      {message.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                    </div>
                    <div
                      className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        message.role === 'user'
                          ? 'bg-cyan-600 text-white rounded-tr-sm'
                          : 'bg-white text-slate-700 border border-slate-200 rounded-tl-sm shadow-sm'
                      }`}
                    >
                      {message.content}
                    </div>
                  </div>
                </div>
                
                {/* Action buttons */}
                {message.actions && message.actions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 ml-9">
                    {message.actions.map((action, actionIdx) => (
                      <button
                        key={actionIdx}
                        onClick={() => handleActionClick(action)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full border border-cyan-200 transition-colors"
                      >
                        {action.label}
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
                    <Bot className="w-3.5 h-3.5 text-cyan-400" />
                  </div>
                  <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Quick questions for first message */}
            {messages.length === 1 && !isLoading && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 ml-9">Quick questions:</p>
                <div className="flex flex-wrap gap-2 ml-9">
                  {quickQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickQuestion(q)}
                      className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-600 text-xs rounded-full border border-slate-200 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question..."
                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                disabled={isLoading}
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 rounded-full bg-cyan-600 hover:bg-cyan-700 p-0 flex items-center justify-center disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2 px-1">
              <button
                onClick={handleNewChat}
                className="text-xs text-slate-400 hover:text-cyan-600 transition-colors"
              >
                Start fresh
              </button>
              <button
                onClick={() => { setIsOpen(false); navigate('/faq'); }}
                className="text-xs text-slate-400 hover:text-cyan-600 transition-colors"
              >
                Browse FAQ
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWidget;
